import { useState } from "react";
import { Check, ArrowRight, Plus, Minus } from "lucide-react";
import { Reveal, ChapterHeading } from "./Reveal";
import { handleAnchorClick } from "@/lib/scroll";

const FAQS = [
    {
        id: "timeline",
        q: "How long does a website take?",
        a: "Small websites usually take just a few days, and bigger ones about a week. I take on up to 4 projects per month, so your site always gets my full attention.",
    },
    {
        id: "payment",
        q: "How does payment work?",
        a: "Simple: 50% to start, 50% at launch. Every project is a fixed quote we agree on before any work begins. No hourly billing, no surprise invoices, ever.",
    },
    {
        id: "included",
        q: "What's included in every project?",
        a: "Custom design, a site that works on every device, basic SEO setup, a working contact form, and launch. You own everything: the design, the code, the domain. No strings attached.",
    },
    {
        id: "changes",
        q: "What if I need changes after launch?",
        a: "Every project includes a revision window before launch and a support period after. If you'd like ongoing updates, I offer simple monthly maintenance plans too.",
    },
];

const FaqItem = ({ item }) => {
    const [open, setOpen] = useState(false);
    return (
        <div
            data-testid={`faq-item-${item.id}`}
            className="border-b border-slate-200 last:border-b-0"
        >
            <button
                type="button"
                data-testid={`faq-toggle-${item.id}`}
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-4 py-5 text-left"
            >
                <span className="text-base font-semibold text-ink">{item.q}</span>
                <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors duration-200 ${
                        open
                            ? "border-brand bg-brand text-white"
                            : "border-slate-200 text-slate-500"
                    }`}
                >
                    {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </span>
            </button>
            {open && (
                <p className="pb-6 pr-12 text-sm leading-relaxed text-slate-600">
                    {item.a}
                </p>
            )}
        </div>
    );
};

const TIERS = [
    {
        id: "starter",
        name: "Starter",
        range: "$150 – $300",
        tag: null,
        blurb: "A smaller website with the same attention to design and quality as every CS Studio project.",
        features: [
            "2–4 custom pages",
            "Custom modern design",
            "Looks great on every device",
            "Basic animations & interactions",
            "Contact form + basic SEO",
            "Launched in a few days",
        ],
        featured: false,
    },
    {
        id: "essential",
        name: "Essential",
        range: "$300 – $500",
        tag: null,
        blurb: "For businesses that need a few solid pages and a more complete online presence.",
        features: [
            "Up to 4 custom pages",
            "Custom design & animations",
            "Help structuring your content",
            "SEO setup + analytics",
            "Two revision rounds",
        ],
        featured: true,
    },
    {
        id: "business",
        name: "Business",
        range: "$500 – $750",
        tag: "Full site",
        blurb: "A complete site built to represent your business and win you work.",
        features: [
            "Up to 8 custom pages",
            "Custom animations & interactions",
            "Easy content editing setup",
            "SEO setup + analytics",
            "Priority support for 30 days",
        ],
        featured: false,
    },
    {
        id: "custom",
        name: "Custom",
        range: "$750 – $2,500",
        tag: "Larger builds",
        blurb: "For larger or more advanced projects that need a custom scope.",
        features: [
            "E-commerce & larger websites",
            "Bookings, payments & integrations",
            "Everything in Business",
            "Custom functionality",
            "Fixed quote agreed upfront",
        ],
        featured: false,
    },
];

const Pricing = () => (
    <section
        id="pricing"
        data-testid="pricing-section"
        className="bg-white py-20 sm:py-28 lg:py-32"
    >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <ChapterHeading
                testId="pricing-heading"
                chapter="03"
                eyebrow="Pricing"
                title="Simple, upfront pricing."
                description="No hourly billing, no hidden costs. Every project is a fixed quote we agree on before any work starts. These ranges give you an honest idea of what to expect."
            />
            <div className="mt-14 grid grid-cols-1 gap-6 sm:mt-16 sm:grid-cols-2 lg:grid-cols-4">
                {TIERS.map((tier, i) => (
                    <Reveal key={tier.id} delay={i * 0.08}>
                        <div
                            data-testid={`pricing-card-${tier.id}`}
                            className={`relative flex h-full flex-col rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1 ${
                                tier.featured
                                    ? "border-brand/40 bg-ice shadow-[0_24px_48px_-28px_rgba(37,99,235,0.35)]"
                                    : "border-slate-200 bg-mist hover:border-brand/30 hover:bg-white hover:shadow-[0_24px_48px_-28px_rgba(37,99,235,0.2)]"
                            }`}
                        >
                            {tier.featured && (
                                <span className="absolute -top-3 left-6 rounded-full bg-brand px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                                    Most popular
                                </span>
                            )}
                            {tier.tag && (
                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-dark">
                                    {tier.tag}
                                </p>
                            )}
                            <h3 className="mt-2 text-lg font-semibold tracking-tight text-ink">
                                {tier.name}
                            </h3>
                            <p className="mt-3 text-2xl font-bold tracking-tight text-ink">
                                {tier.range}
                            </p>
                            <p className="mt-2 text-sm text-slate-500">{tier.blurb}</p>
                            <ul className="mt-6 flex-1 space-y-3 border-t border-slate-200/80 pt-6">
                                {tier.features.map((f) => (
                                    <li
                                        key={f}
                                        className="flex items-start gap-2.5 text-sm text-slate-600"
                                    >
                                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                ))}
            </div>
            <Reveal delay={0.2}>
                <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-mist px-6 py-5 sm:px-8">
                    <p className="text-sm text-slate-600">
                        Not sure which fits? Send a message and I'll recommend
                        the smallest option that does the job.
                    </p>
                    <a
                        href="#contact"
                        data-testid="pricing-cta"
                        onClick={(e) => handleAnchorClick(e, "#contact")}
                        className="group inline-flex items-center gap-1.5 text-sm font-semibold text-ink transition-colors duration-200 hover:text-brand-dark"
                    >
                        Get a fixed quote
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </a>
                </div>
            </Reveal>

            <div
                data-testid="faq-block"
                className="mt-20 grid grid-cols-1 gap-10 border-t border-slate-200 pt-14 sm:mt-24 lg:grid-cols-12"
            >
                <Reveal className="lg:col-span-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-dark">
                        FAQ
                    </p>
                    <h3 className="mt-4 text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                        Quick answers.
                    </h3>
                    <p className="mt-4 text-sm leading-relaxed text-slate-600">
                        The things people ask most before starting. Anything
                        else on your mind?{" "}
                        <a
                            href="#contact"
                            data-testid="faq-ask-link"
                            onClick={(e) => handleAnchorClick(e, "#contact")}
                            className="font-semibold text-ink underline decoration-brand/50 underline-offset-2 transition-colors duration-200 hover:text-brand-dark"
                        >
                            Just ask
                        </a>
                        . I reply within 24 hours.
                    </p>
                </Reveal>
                <Reveal delay={0.12} className="lg:col-span-8">
                    <div className="rounded-2xl border border-slate-200 bg-mist px-6 sm:px-8">
                        {FAQS.map((item) => (
                            <FaqItem key={item.id} item={item} />
                        ))}
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);

export default Pricing;
